import { BED_SIZE_OPTIONS } from "../../../constants";

export { BED_SIZE_OPTIONS };

export const mockFilterOptions = {
  city: [
    "Bengaluru",
    "Mumbai",
    "Delhi",
    "Hyderabad",
    "Chennai",
    "Pune",
    "Kolkata",
    "Jaipur",
  ],
  profession: ["Doctor", "Nurse", "Technician", "Admin"],
  department: [
    "Gynaecology",
    "Pediatrics",
    "Orthopedics",
    "Cardiology",
    "Neurology",
  ],
  hospital: [
    "Milann Hospital",
    "Apollo Hospital",
    "Fortis Hospital",
    "Manipal Hospital",
  ],
  status: [
    "ACTIVE",
    "INACTIVE",
    "SIGNUP",
    "BGVTRIGGERED",
    "BGVFAILED",
    "BGVDELAYED",
  ],
  registration: ["Valid", "Expired", "Not Available"],
  specialization: [
    "Obstetrics",
    "Fertility",
    "Reproductive Endocrinology",
    "Neonatology",
  ],
  qualification: ["MBBS, MD", "MBBS, DGO", "MBBS", "MD", "MS"],
  skills: [],
  experience: ["0-3 yr", "3-6 yrs", "6-10 yrs", "10+ yrs"],
  gender: ["Male", "Female", "Other"],
  bedSize: BED_SIZE_OPTIONS,
  authorityName: [],
};

/* ------------------------------------------------------------------ *
 * Similarity matching engine                                          *
 *                                                                     *
 * Used by the Department / Specialization / Qualification filters in  *
 * "Contains" mode to auto-select every option that is textually       *
 * related to a chosen value. Supports: exact, contains, prefix,       *
 * suffix, word-level, case-insensitive, trimmed and abbreviation      *
 * (acronym) matching. Shared by manual selection and the Jobs-page    *
 * pre-selection so both behave identically.                           *
 * ------------------------------------------------------------------ */

// Short connective words that should never, on their own, link two values.
const SIMILARITY_STOPWORDS = new Set([
  "of",
  "and",
  "the",
  "in",
  "for",
  "to",
  "a",
  "an",
  "with",
  "on",
  "at",
  "by",
  "or",
]);

const normalizeSimilarity = (value) =>
  String(value || "")
    .trim()
    .toLowerCase();

// Split into meaningful tokens: drop punctuation, stopwords and 1-char noise.
const tokenizeSimilarity = (value) =>
  normalizeSimilarity(value)
    .split(/[^a-z0-9]+/i)
    .filter((t) => t.length >= 2 && !SIMILARITY_STOPWORDS.has(t));

// Acronym built from the leading letter of each significant word
// ("Resident Medical Officer" -> "rmo"). Empty for single-word values.
const acronymOf = (value) => {
  const words = normalizeSimilarity(value)
    .split(/[^a-z0-9]+/i)
    .filter((w) => w && !SIMILARITY_STOPWORDS.has(w));
  if (words.length < 2) return "";
  return words.map((w) => w[0]).join("");
};

const compactOf = (value) =>
  normalizeSimilarity(value).replace(/[^a-z0-9]/g, "");

// Decide whether `option` is similar enough to `selected` to be auto-selected.
export const isSimilarOption = (selected, option) => {
  const a = normalizeSimilarity(selected);
  const b = normalizeSimilarity(option);
  if (!a || !b) return false;

  // Exact / contains / prefix / suffix (a substring relationship covers all).
  if (a === b || a.includes(b) || b.includes(a)) return true;

  // Abbreviation: one side's acronym equals the other's compacted form.
  const compactA = compactOf(a);
  const compactB = compactOf(b);
  const acrA = acronymOf(a);
  const acrB = acronymOf(b);
  if ((acrA && acrA === compactB) || (acrB && acrB === compactA)) return true;

  // Word-level overlap: share at least one significant word.
  const tokensA = new Set(tokenizeSimilarity(a));
  if (tokensA.size > 0) {
    const tokensB = tokenizeSimilarity(b);
    if (tokensB.some((t) => tokensA.has(t))) return true;
  }

  return false;
};

// Return every option similar to `value`, always including `value` itself,
// with duplicates removed.
export const findSimilarOptions = (value, options = []) => {
  const valid = options.filter((o) => o && typeof o === "string");
  const matches = valid.filter((o) => isSimilarOption(value, o));
  const hasValue = matches.some(
    (m) => normalizeSimilarity(m) === normalizeSimilarity(value),
  );
  return hasValue ? Array.from(new Set(matches)) : [value, ...matches];
};

// Filter fields the search bar can map comma-separated tokens to.
export const SEARCHABLE_FILTER_KEYS = [
  "city",
  "profession",
  "hospital",
  "status",
  "registration",
  "department",
  "specialization",
  "qualification",
  "authorityName",
  "skills",
];

// For single-token searches, only auto-map to controlled fields to avoid
// accidental picks like a hospital name matching a person's name.
const SINGLE_TOKEN_FILTER_KEYS = [
  "city",
  "profession",
  "department",
  "specialization",
  "qualification",
  "authorityName",
  "skills",
  "status",
  "registration",
];

export const parseSearchTokens = (query) => {
  const trimmed = String(query || "").trim();
  if (!trimmed) return [];
  return trimmed
    .split(",")
    .map((t) => t.trim())
    .filter(Boolean);
};

export const matchSearchTokenToField = (token, optionsByKey) => {
  return matchSearchTokenToFieldForKeys(
    token,
    optionsByKey,
    SEARCHABLE_FILTER_KEYS,
    true,
  );
};

const matchSearchTokenToFieldForKeys = (
  token,
  optionsByKey,
  keys,
  allowSimilar = true,
) => {
  const normalized = normalizeSimilarity(token);
  if (!normalized) return null;

  for (const key of keys) {
    const options = (optionsByKey[key] || []).filter(
      (o) => typeof o === "string" && o.trim(),
    );
    const exact = options.filter((o) => normalizeSimilarity(o) === normalized);
    if (exact.length > 0) {
      const canonical = exact.sort((a, b) => a.length - b.length)[0];
      return { key, values: [canonical] };
    }
  }

  if (!allowSimilar) return null;

  // High-ambiguity fields should not use fuzzy/similar matching from free text.
  // Example: "pratika" should not auto-select hospital "PratikaJobizo Branch".
  const EXACT_ONLY_SIMILARITY_KEYS = new Set(["hospital"]);

  for (const key of keys) {
    if (EXACT_ONLY_SIMILARITY_KEYS.has(key)) continue;
    const options = (optionsByKey[key] || []).filter(
      (o) => typeof o === "string" && o.trim(),
    );
    const similar = options.filter((o) => isSimilarOption(token, o));
    if (similar.length > 0) {
      const canonical = similar.sort((a, b) => a.length - b.length)[0];
      return { key, values: [canonical] };
    }
  }

  return null;
};

export const resolveSearchQuery = (query, optionsByKey) => {
  const trimmed = String(query || "").trim();
  // Single token: keep free-text search, but allow exact auto-map for
  // controlled fields (qualification/status/etc.) so "MBBS" still works.
  if (!trimmed.includes(",")) {
    if (!trimmed) return { fieldUpdates: {}, unmatchedTokens: [] };
    const singleMatch = matchSearchTokenToFieldForKeys(
      trimmed,
      optionsByKey,
      SINGLE_TOKEN_FILTER_KEYS,
      false,
    );
    if (!singleMatch) {
      return { fieldUpdates: {}, unmatchedTokens: [trimmed] };
    }
    return {
      fieldUpdates: { [singleMatch.key]: singleMatch.values },
      unmatchedTokens: [],
    };
  }

  const tokens = parseSearchTokens(trimmed);
  const fieldUpdates = {};
  const unmatchedTokens = [];

  tokens.forEach((token) => {
    const match = matchSearchTokenToField(token, optionsByKey);
    if (match) {
      if (!fieldUpdates[match.key]) fieldUpdates[match.key] = new Set();
      match.values.forEach((v) => fieldUpdates[match.key].add(v));
    } else {
      unmatchedTokens.push(token);
    }
  });

  if (
    tokens.length === 1 &&
    unmatchedTokens.length === 1 &&
    Object.keys(fieldUpdates).length === 0
  ) {
    return { fieldUpdates: {}, unmatchedTokens: [trimmed] };
  }

  return {
    fieldUpdates: Object.fromEntries(
      Object.entries(fieldUpdates).map(([k, set]) => [k, Array.from(set)]),
    ),
    unmatchedTokens,
  };
};

export const initials = (name) =>
  String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((s) => s[0])
    .slice(0, 2)
    .join("")
    .toUpperCase() || "?";

export const filterCandidates = (mockCandidates, search, filters) => {
  return mockCandidates.filter((c) => {
    if (search) {
      const q = search.toLowerCase();
      if (
        !c.name.toLowerCase().includes(q) &&
        !c.id.toLowerCase().includes(q) &&
        !(c.phone && c.phone.includes(q)) &&
        !c.specialization.toLowerCase().includes(q)
      ) {
        return false;
      }
    }

    if (filters.city.length > 0 && !filters.city.includes(c.city)) return false;
    if (
      filters.profession.length > 0 &&
      !filters.profession.includes(c.profession)
    )
      return false;
    if (
      filters.department.length > 0 &&
      !filters.department.includes(c.department)
    )
      return false;
    if (filters.status.length > 0 && !filters.status.includes(c.status))
      return false;
    if (
      filters.registration.length > 0 &&
      !filters.registration.includes(c.regStatus)
    )
      return false;
    if (
      filters.specialization.length > 0 &&
      !filters.specialization.includes(c.specialization)
    )
      return false;
    if (
      filters.qualification.length > 0 &&
      !filters.qualification.includes(c.qualification)
    )
      return false;
    if (
      filters.authorityName?.length > 0 &&
      !filters.authorityName.some(
        (filterName) =>
          String(c.authorityName || "")
            .trim()
            .toLowerCase() ===
          String(filterName || "")
            .trim()
            .toLowerCase(),
      )
    )
      return false;
    if (filters.skills?.length > 0) {
      const candidateSkills = Array.isArray(c.skills) ? c.skills : [];
      if (!filters.skills.some((skill) => candidateSkills.includes(skill))) {
        return false;
      }
    }

    return true;
  });
};

export const sortCandidates = (filtered, sortOrder) => {
  return filtered.sort((a, b) => {
    if (sortOrder === "Most Shifts at this hospital") {
      return (b.workedHere || 0) - (a.workedHere || 0);
    }
    if (sortOrder === "Profile Updated (latest)") {
      return new Date(b.updatedDate || 0) - new Date(a.updatedDate || 0);
    }
    if (sortOrder === "Most Recent Shift") {
      return new Date(b.recentDate || 0) - new Date(a.recentDate || 0);
    }
    return 0;
  });
};

/** Cities that can invite each other freely (NCR cluster). */
export const NCR_CITIES = [
  "NCR",
  "Delhi",
  "Noida",
  "Gurgaon",
  "Gurugram",
  "Faridabad",
  "Ghaziabad",
  "Greater Noida",
];

const normalizeCity = (value) => (value || "").trim().toLowerCase();

export const isNcrCity = (city) => {
  const normalized = normalizeCity(city);
  if (!normalized) return false;
  return NCR_CITIES.some((ncrCity) => normalizeCity(ncrCity) === normalized);
};

/**
 * Invite city rule:
 * - NCR job city ↔ any NCR professional city
 * - Non-NCR → exact city match only
 * - Missing job city → allow (do not block when job city is unavailable)
 */
export const candidateMatchesJobCity = (candidateCity, jobCity) => {
  if (!jobCity || !normalizeCity(jobCity)) return true;
  if (!candidateCity || !normalizeCity(candidateCity)) return false;
  if (isNcrCity(jobCity) && isNcrCity(candidateCity)) return true;
  return normalizeCity(candidateCity) === normalizeCity(jobCity);
};
